(() => {
  if (window.__bespokeHeroVideoScriptLoaded) {
    if (typeof window.__bespokeHeroVideoInit === "function") {
      window.__bespokeHeroVideoInit();
    }
    return;
  }

  window.__bespokeHeroVideoScriptLoaded = true;

  const initializeBespokeHeroVideo = (root) => {
    const firstVideoWrapper = root.querySelector(".js-bespoke-hero-video-wrapper-first");
    const secondVideoWrapper = root.querySelector(".js-bespoke-hero-video-wrapper-second");
    const firstVideo = firstVideoWrapper && firstVideoWrapper.querySelector("video");
    const secondVideo = secondVideoWrapper && secondVideoWrapper.querySelector("video");

    const ctaOverlay = root.querySelector(".js-bespoke-hero-video-cta-overlay");
    const ctaWrapper = root.querySelector(".js-bespoke-hero-video-cta-wrapper");
    const cta = root.querySelector(".js-bespoke-hero-video-cta");
    const fallbackCtaWrapper = root.querySelector(".js-bespoke-hero-video-fallback-cta-wrapper");
    const status = root.querySelector(".js-bespoke-hero-video-status");
    const firstPlayPause = root.querySelector(".js-bespoke-hero-video-play-pause-first");
    const secondPlayPause = root.querySelector(".js-bespoke-hero-video-play-pause-second");

    if (!firstVideoWrapper || !secondVideoWrapper || !firstVideo || !secondVideo || !ctaOverlay || !ctaWrapper || !cta || !firstPlayPause || !secondPlayPause) return;

    if (root.dataset.bespokeHeroVideoInitialized === "true") return;
    root.dataset.bespokeHeroVideoInitialized = "true";

    let firstVideoCleanupTimeout = null;
    let ctaFallbackRevealTimeout = null;
    let ctaFadeOutTimeout = null;
    let ctaRevealAfterVideoStartTimeout = null;
    let hasCtaBeenShown = false;
    let isPlayingSecondVideo = false;
    let hasSecondVideoFailed = false;
    const redirectUrl = root.dataset.ctaUrl;
    const animationStatusText = root.dataset.animationStatusText;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const responsiveMediaQuery = window.matchMedia("(min-width: 768px)");
    const fadeDuration = 500;

    const setVideoWrapperActive = (videoWrapper, active) => {
      videoWrapper.classList.toggle("bespoke-hero-video__video-wrapper--active", active);
      setVideoWrapperAccessibilityState(videoWrapper, active);
    };

    const setVideoWrapperAccessibilityState = (videoWrapper, active) => {
      videoWrapper.setAttribute("aria-hidden", String(!active));

      if (active) {
        videoWrapper.removeAttribute("inert");
      } else {
        videoWrapper.setAttribute("inert", "");
      }
    };

    const setElementHidden = (element, hidden) => {
      element.hidden = hidden;
      element.classList.toggle("hidden", hidden);
    };

    const setCtaWrapperActive = (wrapper, active) => {
      wrapper.classList.toggle("bespoke-hero-video__cta-wrapper--active", active);
      setElementHidden(wrapper, !active);
      wrapper.setAttribute("aria-hidden", String(!active));
    };

    const clearCtaFadeOutTimeout = () => {
      if (!ctaFadeOutTimeout) return;
      window.clearTimeout(ctaFadeOutTimeout);
      ctaFadeOutTimeout = null;
    };

    const clearCtaFallbackRevealTimeout = () => {
      if (!ctaFallbackRevealTimeout) return;
      window.clearTimeout(ctaFallbackRevealTimeout);
      ctaFallbackRevealTimeout = null;
    };

    const activateCtaOverlay = (activeWrapper) => {
      clearCtaFadeOutTimeout();
      setCtaWrapperActive(ctaWrapper, activeWrapper === ctaWrapper);
      setCtaWrapperActive(fallbackCtaWrapper, activeWrapper === fallbackCtaWrapper);
      ctaOverlay.classList.add("bespoke-hero-video__cta-overlay--active");
      ctaOverlay.setAttribute("aria-hidden", "false");
    };

    const deactivateCtaOverlay = () => {
      clearCtaFadeOutTimeout();
      ctaOverlay.classList.remove("bespoke-hero-video__cta-overlay--active");
      ctaOverlay.setAttribute("aria-hidden", "true");
      ctaFadeOutTimeout = window.setTimeout(
        () => {
          ctaFadeOutTimeout = null;
          if (ctaOverlay.classList.contains("bespoke-hero-video__cta-overlay--active")) return;
          setCtaWrapperActive(ctaWrapper, false);
        },
        reducedMotion ? 0 : fadeDuration,
      );
    };

    const showCta = () => {
      if (hasCtaBeenShown) return;
      hasCtaBeenShown = true;
      clearCtaFallbackRevealTimeout();
      if (!reducedMotion && hasFirstVideo && hasSecondVideo && !hasSecondVideoFailed) {
        activateCtaOverlay(ctaWrapper);
        return;
      }

      fallbackToDirectCta();
    };

    const scheduleInitialCtaReveal = () => {
      if (firstVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        window.requestAnimationFrame(showCta);
        return;
      }

      firstVideo.addEventListener("loadeddata", showCta, { once: true });
      firstVideo.addEventListener("playing", showCta, { once: true });
      ctaFallbackRevealTimeout = window.setTimeout(showCta, fadeDuration);
    };

    const updatePlayPauseButton = (button, video) => {
      const isPlaying = !video.paused && !video.ended;
      button.setAttribute("data-state", isPlaying ? "pause" : "play");
      button.setAttribute("aria-pressed", String(isPlaying));
      button.setAttribute("aria-label", isPlaying ? button.dataset.pauseLabel : button.dataset.playLabel);
    };

    const bindPlayPauseButton = (button, video, hideOnEnded = false) => {
      const updateButtonState = () => {
        if (hideOnEnded && video.ended) {
          setElementHidden(button, true);
          return;
        }

        updatePlayPauseButton(button, video);
      };

      button.addEventListener("click", () => {
        if (video.paused || video.ended) {
          loadVideoUrl(video, getVideoUrlForViewport(video));
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
      video.addEventListener("play", updateButtonState);
      video.addEventListener("pause", updateButtonState);
      video.addEventListener("ended", updateButtonState);
      updateButtonState();
    };

    const getResponsiveVideoUrl = (mobileUrl, desktopUrl) => (responsiveMediaQuery.matches ? desktopUrl || mobileUrl : mobileUrl || desktopUrl);

    const getVideoUrlForViewport = (video) => {
      if (video === firstVideo) {
        return getResponsiveVideoUrl(root.dataset.firstVideoMobile, root.dataset.firstVideoDesktop);
      }

      return getResponsiveVideoUrl(root.dataset.secondVideoMobile, root.dataset.secondVideoDesktop);
    };

    const hasFirstVideo = Boolean(getVideoUrlForViewport(firstVideo));
    const hasSecondVideo = Boolean(getVideoUrlForViewport(secondVideo));

    const loadVideoUrl = (video, videoUrl) => {
      if (!videoUrl || video.dataset.currentSource === videoUrl) return;

      const shouldResume = !reducedMotion && !video.paused && !video.ended;
      video.dataset.currentSource = videoUrl;
      if (video === secondVideo) hasSecondVideoFailed = false;
      video.src = videoUrl;
      video.load();

      if (shouldResume) {
        video.play().catch(() => {});
      }
    };

    const updateVideoUrls = () => {
      if (reducedMotion) return;

      loadVideoUrl(firstVideo, getVideoUrlForViewport(firstVideo));
      loadVideoUrl(secondVideo, getVideoUrlForViewport(secondVideo));
    };

    const clearFirstVideoCleanupTimeout = () => {
      if (!firstVideoCleanupTimeout) return;
      window.clearTimeout(firstVideoCleanupTimeout);
      firstVideoCleanupTimeout = null;
    };

    const fallbackToDirectCta = () => {
      clearFirstVideoCleanupTimeout();
      clearCtaFallbackRevealTimeout();
      if (ctaRevealAfterVideoStartTimeout) {
        window.clearTimeout(ctaRevealAfterVideoStartTimeout);
        ctaRevealAfterVideoStartTimeout = null;
      }
      hasCtaBeenShown = true;
      firstVideo.pause();
      secondVideo.pause();
      setVideoWrapperActive(firstVideoWrapper, false);
      setVideoWrapperActive(secondVideoWrapper, false);
      setElementHidden(firstPlayPause, true);
      setElementHidden(secondPlayPause, true);
      activateCtaOverlay(fallbackCtaWrapper);
    };

    const waitForVideoReadyState = (video, readyState) =>
      new Promise((resolve, reject) => {
        if (!video.currentSrc && !video.src) {
          reject(new Error("Video source is missing."));
          return;
        }

        if (video.readyState >= readyState) {
          resolve();
          return;
        }

        let timeout = null;

        const cleanup = () => {
          window.clearTimeout(timeout);
          video.removeEventListener("canplay", handleReady);
          video.removeEventListener("loadeddata", handleReady);
          video.removeEventListener("error", handleError);
        };

        const handleReady = () => {
          if (video.readyState < readyState) return;
          cleanup();
          resolve();
        };

        const handleError = () => {
          cleanup();
          reject(new Error("Video failed to load."));
        };

        timeout = window.setTimeout(() => {
          cleanup();
          reject(new Error("Video preload timed out."));
        }, 8000);

        video.addEventListener("canplay", handleReady);
        video.addEventListener("loadeddata", handleReady);
        video.addEventListener("error", handleError);
      });

    const startVideoExperience = () => {
      if (reducedMotion || !hasFirstVideo || !hasSecondVideo) {
        fallbackToDirectCta();
        return;
      }

      Promise.all([waitForVideoReadyState(firstVideo, HTMLMediaElement.HAVE_CURRENT_DATA), waitForVideoReadyState(secondVideo, HTMLMediaElement.HAVE_FUTURE_DATA)])
        .then(() => {
          return firstVideo.play().catch(() => {});
        })
        .then(() => {
          ctaRevealAfterVideoStartTimeout = window.setTimeout(() => {
            ctaRevealAfterVideoStartTimeout = null;
            scheduleInitialCtaReveal();
          }, fadeDuration);
        })
        .catch(() => {
          fallbackToDirectCta();
        });
    };

    const resetVideoExperience = () => {
      clearFirstVideoCleanupTimeout();
      clearCtaFallbackRevealTimeout();
      clearCtaFadeOutTimeout();

      if (ctaRevealAfterVideoStartTimeout) {
        window.clearTimeout(ctaRevealAfterVideoStartTimeout);
        ctaRevealAfterVideoStartTimeout = null;
      }

      hasCtaBeenShown = false;
      isPlayingSecondVideo = false;
      hasSecondVideoFailed = false;

      firstVideo.pause();
      secondVideo.pause();
      firstVideo.currentTime = 0;
      secondVideo.currentTime = 0;

      firstVideoWrapper.classList.remove("bespoke-hero-video__video-wrapper--no-transition");
      setVideoWrapperActive(firstVideoWrapper, true);
      setVideoWrapperActive(secondVideoWrapper, false);
      setCtaWrapperActive(ctaWrapper, false);
      setCtaWrapperActive(fallbackCtaWrapper, false);
      ctaOverlay.classList.remove("bespoke-hero-video__cta-overlay--active");
      ctaOverlay.setAttribute("aria-hidden", "true");

      if (status) status.textContent = "";

      updateVideoUrls();
      setElementHidden(firstPlayPause, reducedMotion || !hasFirstVideo);
      setElementHidden(secondPlayPause, reducedMotion || !hasSecondVideo);
      updatePlayPauseButton(firstPlayPause, firstVideo);
      updatePlayPauseButton(secondPlayPause, secondVideo);

      startVideoExperience();
    };

    firstVideo.addEventListener("error", () => fallbackToDirectCta());

    secondVideo.addEventListener("error", () => {
      hasSecondVideoFailed = true;
      if (hasCtaBeenShown) fallbackToDirectCta();
    });

    setCtaWrapperActive(fallbackCtaWrapper, false);
    setVideoWrapperActive(firstVideoWrapper, true);
    updateVideoUrls();
    if (responsiveMediaQuery.addEventListener) {
      responsiveMediaQuery.addEventListener("change", updateVideoUrls);
    } else {
      responsiveMediaQuery.addListener(updateVideoUrls);
    }
    bindPlayPauseButton(firstPlayPause, firstVideo);
    bindPlayPauseButton(secondPlayPause, secondVideo, true);

    setElementHidden(firstPlayPause, reducedMotion || !hasFirstVideo);
    setElementHidden(secondPlayPause, reducedMotion || !hasSecondVideo);

    startVideoExperience();

    cta.addEventListener("click", async () => {
      if (isPlayingSecondVideo) return;
      isPlayingSecondVideo = true;
      deactivateCtaOverlay();

      if (!hasSecondVideo || hasSecondVideoFailed) {
        fallbackToDirectCta();
        isPlayingSecondVideo = false;
        return;
      }

      if (status && animationStatusText) status.textContent = animationStatusText;

      try {
        loadVideoUrl(secondVideo, getVideoUrlForViewport(secondVideo));
        await secondVideo.play();

        if (hasSecondVideoFailed) throw new Error("Second video failed to load.");

        setVideoWrapperActive(secondVideoWrapper, true);
        setVideoWrapperAccessibilityState(firstVideoWrapper, false);
        setElementHidden(firstPlayPause, true);
        clearFirstVideoCleanupTimeout();
        firstVideoCleanupTimeout = window.setTimeout(() => {
          firstVideoCleanupTimeout = null;
          firstVideo.pause();
          firstVideoWrapper.classList.add("bespoke-hero-video__video-wrapper--no-transition");
          setVideoWrapperActive(firstVideoWrapper, false);
          window.requestAnimationFrame(() => {
            firstVideoWrapper.classList.remove("bespoke-hero-video__video-wrapper--no-transition");
          });
        }, fadeDuration);
      } catch {
        hasSecondVideoFailed = true;
        fallbackToDirectCta();
      } finally {
        isPlayingSecondVideo = false;
      }
    });

    secondVideo.addEventListener("ended", () => {
      setElementHidden(secondPlayPause, true);
      window.location.href = redirectUrl;
    });

    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;
      resetVideoExperience();
    });
  };

  const initializeBespokeHeroVideoSection = (scope = document) => {
    const root = typeof scope.matches === "function" && scope.matches(".js-bespoke-hero-video") ? scope : scope.querySelector(".js-bespoke-hero-video");

    if (root) initializeBespokeHeroVideo(root);
  };

  const initializeBespokeHeroVideoOnDomReady = (scope = document) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => initializeBespokeHeroVideoSection(scope), { once: true });
    } else {
      initializeBespokeHeroVideoSection(scope);
    }
  };

  window.__bespokeHeroVideoInit = initializeBespokeHeroVideoOnDomReady;
  initializeBespokeHeroVideoOnDomReady();
  document.addEventListener("shopify:section:load", (event) => initializeBespokeHeroVideoSection(event.target));
})();
